import { ReactNode, useEffect } from 'react';
import { create } from 'zustand';

/**
 * Per-page header slots. Pages inject content via
 * `useHeaderSlot({ left, right })` and AppShell.Header renders both slots
 * in the corresponding positions. On unmount each slot is cleared so the
 * next route starts blank.
 *
 * Why a global store instead of context: the header lives ABOVE the
 * <Outlet/> in the tree, so child routes can't pass content upward via
 * context without provider gymnastics. A small Zustand store keeps the
 * wiring trivial.
 */
interface HeaderSlotState {
  left: ReactNode;
  right: ReactNode;
  setLeft(node: ReactNode): void;
  setRight(node: ReactNode): void;
}

export const useHeaderSlotStore = create<HeaderSlotState>(set => ({
  left: null,
  right: null,
  setLeft: node => set({ left: node }),
  setRight: node => set({ right: node }),
}));

export function useHeaderSlot(slots: { left?: ReactNode; right?: ReactNode }): void {
  const setLeft = useHeaderSlotStore(s => s.setLeft);
  const setRight = useHeaderSlotStore(s => s.setRight);
  useEffect(() => {
    if (slots.left !== undefined) setLeft(slots.left);
    if (slots.right !== undefined) setRight(slots.right);
    return () => {
      if (slots.left !== undefined) setLeft(null);
      if (slots.right !== undefined) setRight(null);
    };
  }, [slots.left, slots.right, setLeft, setRight]);
}

export function HeaderLeftSlot() {
  return <>{useHeaderSlotStore(s => s.left)}</>;
}

export function HeaderRightSlot() {
  return <>{useHeaderSlotStore(s => s.right)}</>;
}
